'use client';

import { StoryblokServerComponent } from '@storyblok/react/rsc';
import { FC, useState } from 'react';
import { Loader } from 'atoms/Loader';
import { Filters } from 'organisms/Filters';
import { FiltersProvider } from 'organisms/Filters/Context';
import { EmptyResult } from 'organisms/Filters/EmptyResult/EmptyResult';
import { useFilterState } from 'organisms/Filters/hooks';
import { filterQueryParameters } from 'organisms/Filters/utils';
import {
  ProductGridContainer,
  ProductGridTitle,
} from 'organisms/ProductGridLayout';
import { ProductTile } from 'organisms/ProductTile';
import { InView } from 'pages/ProductListing/InView';
import { LoadMoreNext } from 'pages/ProductListing/LoadMoreNext';
import { LoadMorePrev } from 'pages/ProductListing/LoadMorePrev';
import { useGtmInternalSearch } from 'modules/gtm/hooks';
import { useGTMItemListView } from 'modules/gtm/modules/sections/productList/hooks';
import { useGtmAutomaticallyScroll } from 'modules/gtm/modules/sections/scroll/hooks';
import { addQueryParamsWithoutReload, getQueryParam } from 'modules/main/utils';
import { mergeBannerWithProduct } from 'modules/shop/banners/adapter';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import {
  ISortBy,
  SortOptions,
  SortOrder,
} from 'commerce/factFinder/queryParameters';
import { Facets, IFilter } from 'commerce/factFinder/types';
import { getCategoryProductsClient } from 'commerce/shop/client';
import { useProductSections } from './hooks/useProductSections';
import { IProductGridProps } from './types';
import styles from './ProductGrid.module.css';

export const ProductListing: FC<IProductGridProps> = ({
  title,
  facets = [],
  initProducts = [],
  initSection = 1,
  lastPage = 1,
  sortItems = [],
  totalProducts,
  categoryName,
  configuration,
  gtmPageName,
  initFilters = [],
  initBanners = [],
  initQuery,
  upsellingBanners,
}) => {
  const { gtmData } = useSiteConfig();
  const [loading, setLoading] = useState(false);
  const data = facets.find(
    ({ associatedFieldName }) => associatedFieldName === Facets.PRICE,
  );
  const [stateFilters, setFilters] = useFilterState({
    initFilters,
    exclude: [Facets.CATEGORY_NAMES],
    currencySymbol: data?.unit,
  });
  const [sortBy, setSortBy] = useState<ISortBy>({
    field: SortOptions.relevancy,
    direction: SortOrder.desc,
  });

  const {
    productSections,
    setProductSections,
    pushProductSection,
    unshiftProductSection,
    setCurrentSection,
    isLoadMorePrevAvailable,
    isLoadMoreNextAvailable,
  } = useProductSections({
    page: initSection,
    initProducts,
    initBanners,
    categoryName,
    lastPage,
    withBanners: !initQuery,
  });

  /** gtm events*/
  const { addProductToGTMList } = useGTMItemListView();

  useGtmInternalSearch(totalProducts);

  useGtmAutomaticallyScroll(gtmData?.pageType);

  const handleFilter = async (filters: IFilter[]) => {
    setLoading(true);

    const params = {
      page: 1,
      sortBy,
      filters: filterQueryParameters(filters),
      categoryName,
    };
    addQueryParamsWithoutReload(params);
    const { data, error } = await getCategoryProductsClient(params);

    setLoading(false);

    if (error) {
      console.error(error);
      return;
    }

    setCurrentSection({
      page: 1,
      lastPage: data.paging?.pageCount,
    });
    setProductSections([
      {
        page: 1,
        products: data.hits || [],
        banners: productSections[0].banners,
      },
    ]);
  };

  const handleVisibleTile = (page: number) => {
    const querySection = getQueryParam('page');
    if (initSection !== lastPage && querySection && !isNaN(initSection)) {
      setCurrentSection({
        page,
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      {title && (
        <ProductGridTitle className={styles.title} color="purple">
          {title}
        </ProductGridTitle>
      )}

      {loading && <Loader className={styles.loader} />}
      {categoryName && configuration?.showFilters && (
        <FiltersProvider
          value={{
            facets,
            filters: stateFilters,
            sortOptions: sortItems,
            sortBy,
            setSortBy,
            setFilters,
            handleSearch: () => handleFilter(stateFilters),
            handleReset() {
              handleFilter([]);
              setFilters([]);
              setSortBy({
                field: SortOptions.relevancy,
                direction: SortOrder.desc,
              });
            },
          }}
        >
          <Filters />
        </FiltersProvider>
      )}
      {productSections[0]?.products.length < 1 ? (
        <EmptyResult />
      ) : (
        <>
          {isLoadMorePrevAvailable && (
            <LoadMorePrev
              setNewPage={async (page) => {
                await unshiftProductSection(
                  page,
                  filterQueryParameters(stateFilters),
                  sortBy,
                );
                setCurrentSection({ page });
              }}
              pageType={gtmData?.pageType}
            />
          )}
          <ProductGridContainer>
            {productSections.map(({ products, banners, page }) =>
              mergeBannerWithProduct(products, banners)?.map((entity) => {
                if (
                  'component' in entity &&
                  entity.component === 'banner-tile'
                ) {
                  return (
                    <StoryblokServerComponent key={entity._uid} blok={entity} />
                  );
                } else if (!('component' in entity)) {
                  return (
                    <InView
                      key={entity.id}
                      onVisible={() => {
                        handleVisibleTile(page);
                        addProductToGTMList(entity);
                      }}
                      enable
                    >
                      <ProductTile
                        product={{
                          ...entity,
                          isPrimaryPage: configuration?.isPrimaryPage,
                        }}
                        gtmCategoryName={gtmPageName}
                        displaySalePrices={configuration?.displaySalePrices}
                        upsellingBanner={upsellingBanners?.find((banner) =>
                          banner.productIds.includes(entity.referenceKey),
                        )}
                      />
                    </InView>
                  );
                } else {
                  return null;
                }
              }),
            )}
          </ProductGridContainer>

          {isLoadMoreNextAvailable && (
            <LoadMoreNext
              setNewPage={async (page) => {
                await pushProductSection(
                  page,
                  filterQueryParameters(stateFilters),
                  sortBy,
                  initQuery,
                );
                setCurrentSection({ page });
              }}
              pageType={gtmData?.pageType}
            />
          )}
        </>
      )}
    </div>
  );
};
